import React from 'react';
import { Book, Users, Heart, Sparkles, Music, MessageCircle, HandHeart, Cross } from 'lucide-react';

const WhyWeDoPage = () => {
  const acts242Elements = [
    {
      icon: Book,
      title: 'Apostles\' Doctrine',
      verse: 'Acts 2:42',
      description: 'Devoted to systematic, expository teaching of God\'s Word',
      practice: '30 minutes of expository sermon each Sunday'
    },
    {
      icon: Users,
      title: 'Fellowship',
      verse: 'Acts 2:42, 46',
      description: 'Breaking bread together and sharing life in authentic community',
      practice: 'Sunday meal fellowship and weekly gatherings'
    },
    {
      icon: HandHeart,
      title: 'Breaking of Bread',
      verse: 'Acts 2:42, 46; 1 Cor 11:23-26',
      description: 'Remembering Christ\'s sacrifice through the Lord\'s Table',
      practice: 'Weekly communion during Sunday worship'
    },
    {
      icon: Heart,
      title: 'Prayers',
      verse: 'Acts 2:42',
      description: 'Corporate and individual prayer as the foundation of ministry',
      practice: 'Intercessory prayer, Wednesday prayer meetings, Monday online devotions'
    }
  ];

  const orderOfService = [
    {
      time: '20 min',
      title: 'Worship Through Songs',
      icon: Music,
      description: 'Corporate worship preparing hearts to receive God\'s Word',
      biblical: 'Psalm 95:1-2, Colossians 3:16'
    },
    {
      time: '10 min',
      title: 'Worship Exhortation',
      icon: MessageCircle,
      description: 'Brief teaching on the nature and practice of worship',
      biblical: 'John 4:23-24'
    },
    {
      time: '10 min',
      title: 'Individual Worship',
      icon: Heart,
      description: 'Time for personal meditation and worship',
      biblical: 'Psalm 46:10'
    },
    {
      time: '30 min',
      title: 'Expository Sermon',
      icon: Book,
      description: 'Verse-by-verse teaching of Scripture',
      biblical: '2 Timothy 4:2, Nehemiah 8:8'
    },
    {
      time: '10 min',
      title: 'Communion & Intercessory Prayer',
      icon: HandHeart,
      description: 'Partaking of bread and cup in remembrance, followed by corporate prayer',
      biblical: 'Luke 22:19-20, James 5:16'
    },
    {
      time: '10 min',
      title: 'Announcements, Offering & Closing Prayer',
      icon: Sparkles,
      description: 'Church news, offering, closing song, prayer, and benediction',
      biblical: 'Hebrews 10:24-25, Numbers 6:24-26'
    }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20">
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
              Why We Do What We Do
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our worship and fellowship patterns are rooted in the New Testament church described in Acts 2:42, 
              following the heritage revealed to Bro. Bakht Singh by the Lord Jesus Christ.
            </p>
          </div>
        </div>
      </section>

      {/* Heritage Section - MOVED TO TOP */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">
              Our Spiritual Heritage
            </h2>
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <Cross className="w-12 h-12 text-[#f17b21] flex-shrink-0" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">The Acts 2:42 Pattern Revealed to Bro. Bakht Singh</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Bro. Bakht Singh (1903-2000) was a pioneering servant of God in India who received direct revelation 
                    from the Lord Jesus Christ regarding the New Testament pattern for church life as described in Acts 2:42. 
                    Through prayer, fasting, and deep study of Scripture, the Lord showed him how the early church operated 
                    and how we should gather today.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    This wasn't merely following tradition, but a divine revelation of biblical truth. The Lord revealed 
                    to him that true church life must center on four essential elements: the apostles' doctrine (systematic 
                    Bible teaching), fellowship (authentic community), breaking of bread (weekly communion), and prayers 
                    (corporate intercession).
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The Hebron Church movement that emerged from this revelation emphasized:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                    <li>Indigenous leadership raised from within the local assembly</li>
                    <li>Expository preaching that systematically teaches through books of the Bible</li>
                    <li>Holy living and separation from worldly practices</li>
                    <li>Fervent corporate prayer as the foundation of all ministry</li>
                    <li>Weekly breaking of bread as the central act of worship</li>
                    <li>The priesthood of all believers - every member actively participating</li>
                    <li>Simplicity in worship without religious ceremony or ritual</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    At El Bethel Church, we follow this same pattern not out of mere imitation, but because we believe 
                    this is the biblical pattern revealed by Christ Himself for His church. We seek to maintain the 
                    simplicity, authenticity, and Spirit-led nature of New Testament Christianity as practiced by the 
                    early believers and rediscovered through Bro. Bakht Singh's ministry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Acts 2:42 Foundation - Compact Version */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-gray-600 mb-6">
              "And they continued steadfastly in the apostles' doctrine and fellowship, 
              and in breaking of bread, and in prayers." - <span className="font-semibold text-gray-800">Acts 2:42</span>
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {acts242Elements.map((element, index) => {
                const Icon = element.icon;
                return (
                  <div key={index} className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow">
                    <Icon className="w-8 h-8 text-[#f17b21] mb-2 mx-auto" />
                    <h3 className="text-sm font-bold text-gray-800">{element.title}</h3>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sunday Order of Service */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">
              Sunday Order of Service
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center">
              Our 90-minute worship gathering (5:00 PM - 6:30 PM) follows a deliberate pattern 
              that allows space for worship, teaching, communion, and prayer.
            </p>
            
            <div className="space-y-3">
              {orderOfService.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-gradient-to-r from-orange-50 to-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-[#f17b21] rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{item.time}</span>
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-5 h-5 text-[#f17b21]" />
                          <h3 className="text-lg font-bold text-gray-800">{item.title}</h3>
                        </div>
                        <p className="text-gray-700 text-sm mb-1">{item.description}</p>
                        <p className="text-xs text-gray-500 italic">{item.biblical}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Why This Matters
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Our worship structure isn't about tradition for tradition's sake. It's about creating space for:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <Sparkles className="w-8 h-8 text-[#f17b21] mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 mb-2">Encountering God</h3>
                <p className="text-sm text-gray-600">Through worship, Word, and communion</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <Users className="w-8 h-8 text-[#f17b21] mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 mb-2">Building Community</h3>
                <p className="text-sm text-gray-600">Authentic relationships centered on Christ</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <Book className="w-8 h-8 text-[#f17b21] mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 mb-2">Growing in Truth</h3>
                <p className="text-sm text-gray-600">Deep understanding of God's Word</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-[#f17b21] to-orange-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Experience It Yourself</h2>
            <p className="text-lg mb-6">
              Join us this Sunday and discover what worship according to God's pattern looks like.
            </p>
            <a 
              href="/visit"
              className="inline-block bg-white text-[#f17b21] hover:bg-orange-50 px-8 py-4 rounded-lg font-semibold transition-colors"
            >
              Plan Your Visit
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyWeDoPage;
